/**
 * Prototype catalogue of the national planning frameworks and the processes run under each.
 *
 * Everything here is hardcoded sample content for the prototype — the frameworks, their tier
 * in the national hierarchy, and every process (pillars, chapters, M&E cycle steps, projects,
 * conventions). Figures are illustrative, not official statistics.
 */

export type FrameworkKey =
  | 'vision'
  | 'fydp'
  | 'manifesto'
  | 'strategic-plans'
  | 'flagship'
  | 'international';

/**
 * How a process is shown:
 * - overview: framework dashboard built from its area processes
 * - area: a pillar, priority area, chapter or aspiration with its indicators
 * - matrix: an implementation matrix of interventions and their financing
 * - cycle: an M&E cycle step tracked per institution (no step = cycle overview)
 * - projects: flagship projects (no category = portfolio dashboard)
 * - register: reporting obligations such as international conventions
 * - link: an existing page elsewhere in the module
 */
export type ProcessKind = 'overview' | 'area' | 'matrix' | 'cycle' | 'projects' | 'register' | 'link';

export interface Indicator {
  id: string;
  name: string;
  unit: string;
  lead: string;
  baseline: number;
  baselineYear: number;
  midTarget: number;
  target: number;
  targetYear: number;
  /** Latest reported value; null until something is reported. */
  actual: number | null;
  /** Milestone targets the indicator is delivered through */
  targets?: Target[];
}

export type ReportType = 'INPUT' | 'OUTPUT' | 'PROCESS' | 'RESULT';

export interface ImplementationReport {
  id: string;
  title: string;
  type: ReportType;
  year: number;
  quarter: 1 | 2 | 3 | 4;
  submittedOn: string;
  reportedBy: string;
  summary: string;
  /** Value achieved, in the target's unit */
  value: number | null;
  /** Progress to target at the time of the report, 0–100 */
  progress: number;
  archived: boolean;
}

export interface PlanPoint {
  year: number;
  value: number;
}

export interface Target {
  id: string;
  name: string;
  unit: string;
  startDate: string;
  dueDate: string;
  baseline: number | null;
  baselineYear: number | null;
  targetValue: number;
  /** Planned value per year; empty until a plan is set */
  plan: PlanPoint[];
  progress: number;
  reports: ImplementationReport[];
}

export interface RegionScore {
  name: string;
  score: number;
}

export type InterventionReportKind = 'FINANCIAL' | 'PROGRESS';

/**
 * A financial report records funds released and spent in a period (TZS billions); a
 * progress report records cumulative financial and physical progress (%).
 */
export interface InterventionReport {
  id: string;
  kind: InterventionReportKind;
  year: number;
  quarter: 1 | 2 | 3 | 4;
  submittedOn: string;
  reportedBy: string;
  released: number | null;
  spent: number | null;
  financial: number | null;
  physical: number | null;
  summary: string;
}

export interface MatrixArea {
  name: string;
  summary: string;
}

export interface Intervention {
  id: string;
  implementer: string;
  area: string;
  intervention: string;
  target: string;
  project: string;
  /** TZS billions by source of finance */
  government: number;
  dps: number;
  privateSector: number;
  expectedOutput: string;
  financial: number;
  physical: number;
  reports?: InterventionReport[];
}

export interface CycleStep {
  purpose: string;
  frequency: string;
  lead: string;
  activities: string[];
  outputs: string[];
}

export type StepStatus = 'COMPLETE' | 'IN_PROGRESS' | 'OVERDUE' | 'NOT_STARTED';

export type InstitutionCategory = 'Ministry' | 'Agency' | 'LGA';

export interface PlanInstitution {
  id: string;
  name: string;
  shortName: string;
  category: InstitutionCategory;
}

export type ProjectStatus = 'COMPLETED' | 'ON_TRACK' | 'AT_RISK' | 'DELAYED' | 'PREPARATION';

export interface Milestone {
  name: string;
  due: string;
  done: boolean;
}

export type ActivityStatus = 'PENDING' | 'IN_PROGRESS' | 'ON_HOLD' | 'COMPLETED' | 'DELAYED';

export interface SubActivity {
  id: string;
  title: string;
  done: boolean;
}

export interface ProjectActivity {
  id: string;
  title: string;
  description: string;
  start: string;
  end: string;
  /** TZS billions */
  budget: number;
  disbursed: number;
  spent: number;
  completion: number;
  /** Completion at the report before the latest, for the period comparison */
  previousCompletion: number;
  status: ActivityStatus;
  reporter: string | null;
  subActivities: SubActivity[];
}

export interface SiteEvidence {
  id: string;
  caption: string;
  activityId: string | null;
  date: string;
  lat: number;
  lng: number;
  /** Data URL of an uploaded photo; null shows a placeholder */
  photo: string | null;
}

export interface ImplementingInstitution {
  shortName: string;
  name: string;
  role: string;
}

export interface FlagshipProject {
  id: string;
  name: string;
  shortName: string;
  category: string;
  implementer: string;
  contractor: string;
  location: string;
  coords: [number, number];
  financing: string;
  /** TZS billions */
  budget: number;
  disbursed: number;
  physical: number;
  financial: number;
  status: ProjectStatus;
  start: string;
  end: string;
  contractSigned: string;
  /** Approved extension-of-time completion dates, in order */
  extensions: string[];
  institutions: ImplementingInstitution[];
  activities: ProjectActivity[];
  evidence: SiteEvidence[];
}

/** Hand-written part of a project; activities and the rest are generated from its milestones. */
type ProjectSeed = Omit<
  FlagshipProject,
  'contractSigned' | 'extensions' | 'institutions' | 'activities' | 'evidence'
> & { milestones: Milestone[] };

export type ObligationStatus = 'SUBMITTED' | 'ON_TRACK' | 'DUE_SOON' | 'OVERDUE';

export interface RegisterItem {
  id: string;
  shortName: string;
  name: string;
  lead: string;
  cycle: string;
  lastReport: string;
  nextDue: string;
  status: ObligationStatus;
}

export interface FrameworkProcess {
  key: string;
  name: string;
  icon: string;
  kind: ProcessKind;
  /** Heading the process sits under in the process rail */
  group: string;
  summary: string;
  indicators?: Indicator[];
  regions?: RegionScore[];
  interventions?: Intervention[];
  /** Priority areas of an implementation matrix, in display order */
  matrixAreas?: MatrixArea[];
  step?: CycleStep;
  /** Flagship project sector this process filters to */
  category?: string;
  items?: RegisterItem[];
  /** Absolute route for link processes */
  route?: string;
}

export interface ReviewStats {
  scheduled: number;
  conducted: number;
  directives: number;
  implemented: number;
}

export interface FrameworkDefinition {
  key: FrameworkKey;
  tier: 1 | 2 | 3;
  /** Card title, as on the national landing page */
  title: string;
  subtitle: string;
  shortName: string;
  name: string;
  period: string;
  icon: string;
  description: string;
  /** Frameworks one tier up that this one implements */
  implements: FrameworkKey[];
  reviews?: ReviewStats;
  processes: FrameworkProcess[];
}

export interface FrameworkTier {
  tier: 1 | 2 | 3;
  label: string;
  hint: string;
}

export const FRAMEWORK_TIERS: FrameworkTier[] = [
  {
    tier: 1,
    label: 'Long-term vision',
    hint: 'The national destination every other framework serves.',
  },
  {
    tier: 2,
    label: 'Medium-term plans',
    hint: 'Five-year plans and political commitments that implement the Vision.',
  },
  {
    tier: 3,
    label: 'Delivery & commitments',
    hint: 'Institutional plans, flagship projects and international agendas that deliver the tiers above.',
  },
];

// ── Helpers ─────────────────────────────────────────────────────────────────────

/** Share of the way from baseline to target, 0–100. Works for indicators that should fall. */
export function indicatorProgress(indicator: Indicator): number | null {
  if (indicator.actual === null) return null;
  const span = indicator.target - indicator.baseline;
  if (span === 0) return 100;
  const share = ((indicator.actual - indicator.baseline) / span) * 100;
  return Math.max(0, Math.min(100, Math.round(share)));
}

export function averageProgress(indicators: Indicator[]): number | null {
  const values = indicators.map(indicatorProgress).filter((v): v is number => v !== null);
  if (!values.length) return null;
  return Math.round(values.reduce((sum, v) => sum + v, 0) / values.length);
}

export type ProgressBand = 'high' | 'medium' | 'low' | 'none';

/** Same cut-offs as the SDG tracker: under 45%, 45–64%, 65% and up. */
export function progressBand(progress: number | null): ProgressBand {
  if (progress === null) return 'none';
  if (progress >= 65) return 'high';
  if (progress >= 45) return 'medium';
  return 'low';
}

export function bandText(progress: number | null): string {
  switch (progressBand(progress)) {
    case 'high':
      return 'text-emerald-600';
    case 'medium':
      return 'text-amber-600';
    case 'low':
      return 'text-rose-600';
    default:
      return 'text-slate-400';
  }
}

export function bandFill(progress: number | null): string {
  switch (progressBand(progress)) {
    case 'high':
      return 'bg-emerald-500';
    case 'medium':
      return 'bg-amber-500';
    case 'low':
      return 'bg-rose-500';
    default:
      return 'bg-slate-300';
  }
}

/** Share of a project's contract period already used, 0–100. */
export function projectTimeElapsed(project: FlagshipProject): number {
  const start = new Date(project.start).getTime();
  const end = new Date(project.end).getTime();
  if (end <= start) return 100;
  const share = ((Date.now() - start) / (end - start)) * 100;
  return Math.max(0, Math.min(100, Math.round(share)));
}

/** "in 40 days" / "12 days overdue" relative to today. */
export function dueLabel(date: string): string {
  const days = Math.round((new Date(date).getTime() - Date.now()) / (24 * 60 * 60 * 1000));
  if (days === 0) return 'due today';
  return days > 0 ? `in ${days} days` : `${-days} days overdue`;
}

function ind(
  id: string,
  name: string,
  unit: string,
  lead: string,
  baseline: number,
  baselineYear: number,
  midTarget: number,
  target: number,
  targetYear: number,
  actual: number | null,
): Indicator {
  return { id, name, unit, lead, baseline, baselineYear, midTarget, target, targetYear, actual };
}

// ── Institutions reporting strategic plans ──────────────────────────────────────

export const PLAN_INSTITUTIONS: PlanInstitution[] = [
  { id: 'mof', name: 'Ministry of Finance', shortName: 'MoF', category: 'Ministry' },
  { id: 'moh', name: 'Ministry of Health', shortName: 'MoH', category: 'Ministry' },
  { id: 'moest', name: 'Ministry of Education, Science and Technology', shortName: 'MoEST', category: 'Ministry' },
  { id: 'moa', name: 'Ministry of Agriculture', shortName: 'MoA', category: 'Ministry' },
  { id: 'moe', name: 'Ministry of Energy', shortName: 'MoE', category: 'Ministry' },
  { id: 'maji', name: 'Ministry of Water', shortName: 'MoW', category: 'Ministry' },
  { id: 'tra', name: 'Tanzania Revenue Authority', shortName: 'TRA', category: 'Agency' },
  { id: 'tanesco', name: 'Tanzania Electric Supply Company', shortName: 'TANESCO', category: 'Agency' },
  { id: 'tpa', name: 'Tanzania Ports Authority', shortName: 'TPA', category: 'Agency' },
  { id: 'trc', name: 'Tanzania Railways Corporation', shortName: 'TRC', category: 'Agency' },
  { id: 'tcra', name: 'Tanzania Communications Regulatory Authority', shortName: 'TCRA', category: 'Agency' },
  { id: 'ega', name: 'e-Government Authority', shortName: 'eGA', category: 'Agency' },
  { id: 'nida', name: 'National Identification Authority', shortName: 'NIDA', category: 'Agency' },
  { id: 'msd', name: 'Medical Stores Department', shortName: 'MSD', category: 'Agency' },
  { id: 'heslb', name: "Higher Education Students' Loans Board", shortName: 'HESLB', category: 'Agency' },
  { id: 'dodoma-cc', name: 'Dodoma City Council', shortName: 'Dodoma CC', category: 'LGA' },
  { id: 'dsm-cc', name: 'Dar es Salaam City Council', shortName: 'DSM CC', category: 'LGA' },
  { id: 'mwanza-cc', name: 'Mwanza City Council', shortName: 'Mwanza CC', category: 'LGA' },
  { id: 'arusha-cc', name: 'Arusha City Council', shortName: 'Arusha CC', category: 'LGA' },
  { id: 'morogoro-mc', name: 'Morogoro Municipal Council', shortName: 'Morogoro MC', category: 'LGA' },
];

/**
 * Deterministic sample status for one institution at one cycle step — early steps are
 * mostly complete, later ones mostly not started.
 */
export function seedStepStatus(stepIndex: number, institutionIndex: number): StepStatus {
  const score = ((stepIndex + 1) * 37 + (institutionIndex + 1) * 53) % 100;
  const completeCut = 88 - stepIndex * 6;
  if (score < completeCut) return 'COMPLETE';
  if (score < completeCut + 14) return 'IN_PROGRESS';
  if (score < completeCut + 20) return 'OVERDUE';
  return 'NOT_STARTED';
}

// ── Flagship projects ───────────────────────────────────────────────────────────

const PROJECT_SEEDS: ProjectSeed[] = [
  {
    id: 'jnhpp',
    name: 'Julius Nyerere Hydropower Project',
    shortName: 'JNHPP',
    category: 'energy',
    implementer: 'TANESCO',
    location: 'Rufiji, Pwani & Morogoro',
    contractor: 'Arab Contractors – Elsewedy Electric JV',
    coords: [-7.8, 37.85],
    financing: 'Government',
    budget: 6558,
    disbursed: 6290,
    physical: 98,
    financial: 96,
    status: 'ON_TRACK',
    start: '2019-06-15',
    end: '2026-12-31',
    milestones: [
      { name: 'Main dam and spillway', due: '2023-06-30', done: true },
      { name: 'Powerhouse and generating units 1–6', due: '2025-03-31', done: true },
      { name: 'Generating units 7–9 and switchyard', due: '2026-06-30', done: false },
    ],
  },
  {
    id: 'sgr',
    name: 'Standard Gauge Railway, Dar es Salaam – Mwanza',
    shortName: 'SGR',
    category: 'transport',
    implementer: 'TRC',
    location: 'Dar es Salaam – Mwanza',
    contractor: 'Yapı Merkezi; CCECC',
    coords: [-6.82, 39.27],
    financing: 'Government & DFIs',
    budget: 23300,
    disbursed: 15400,
    physical: 78,
    financial: 66,
    status: 'ON_TRACK',
    start: '2017-02-01',
    end: '2027-06-30',
    milestones: [
      { name: 'Lot 1: Dar es Salaam – Morogoro', due: '2024-06-30', done: true },
      { name: 'Lot 2: Morogoro – Makutupora', due: '2024-08-31', done: true },
      { name: 'Lots 3–5: Makutupora – Mwanza', due: '2027-06-30', done: false },
    ],
  },
  {
    id: 'eacop',
    name: 'East African Crude Oil Pipeline',
    shortName: 'EACOP',
    category: 'energy',
    implementer: 'EACOP Ltd / TPDC',
    location: 'Kagera – Tanga',
    contractor: 'Multiple EPC contractors',
    coords: [-5.07, 39.1],
    financing: 'Joint venture',
    budget: 9100,
    disbursed: 5100,
    physical: 61,
    financial: 56,
    status: 'AT_RISK',
    start: '2022-02-01',
    end: '2026-12-31',
    milestones: [
      { name: 'Marine storage terminal', due: '2025-06-30', done: true },
      { name: 'Pipeline laying and welding', due: '2026-03-31', done: false },
      { name: 'Commissioning and first oil', due: '2026-12-31', done: false },
    ],
  },
  {
    id: 'lng',
    name: 'Liquefied Natural Gas Project',
    shortName: 'LNG',
    category: 'energy',
    implementer: 'TPDC',
    location: 'Lindi',
    contractor: 'To be procured',
    coords: [-10.0, 39.71],
    financing: 'Joint venture',
    budget: 95000,
    disbursed: 1100,
    physical: 6,
    financial: 1,
    status: 'PREPARATION',
    start: '2024-07-01',
    end: '2032-06-30',
    milestones: [
      { name: 'Host Government Agreement negotiations', due: '2025-12-31', done: false },
      { name: 'Front-end engineering and investment decision', due: '2027-06-30', done: false },
      { name: 'Resettlement and land acquisition', due: '2027-12-31', done: false },
    ],
  },
  {
    id: 'rea',
    name: 'Rural Electrification Programme (REA III)',
    shortName: 'REA',
    category: 'energy',
    implementer: 'Rural Energy Agency',
    location: 'Nationwide',
    contractor: 'Multiple regional contractors',
    coords: [-6.16, 35.75],
    financing: 'Government & DPs',
    budget: 3040,
    disbursed: 2620,
    physical: 89,
    financial: 86,
    status: 'ON_TRACK',
    start: '2019-07-01',
    end: '2026-12-31',
    milestones: [
      { name: 'Village connections', due: '2024-12-31', done: true },
      { name: 'Hamlet connections — round 1', due: '2025-12-31', done: true },
      { name: 'Hamlet connections — round 2', due: '2026-12-31', done: false },
    ],
  },
  {
    id: 'kigongo-busisi',
    name: 'Kigongo – Busisi Bridge',
    shortName: 'Kigongo–Busisi',
    category: 'transport',
    implementer: 'TANROADS',
    location: 'Mwanza',
    contractor: 'CCECC – China Railway 15th Bureau Group',
    coords: [-2.73, 32.86],
    financing: 'Government',
    budget: 716,
    disbursed: 698,
    physical: 100,
    financial: 97,
    status: 'COMPLETED',
    start: '2020-02-25',
    end: '2025-06-30',
    milestones: [
      { name: 'Piers and foundations', due: '2022-12-31', done: true },
      { name: 'Deck and cable stays', due: '2024-12-31', done: true },
      { name: 'Finishing works and opening', due: '2025-06-30', done: true },
    ],
  },
  {
    id: 'dmgp',
    name: 'Dar es Salaam Port Modernisation',
    shortName: 'DMGP',
    category: 'transport',
    implementer: 'TPA',
    location: 'Dar es Salaam',
    contractor: 'China Harbour Engineering Company',
    coords: [-6.83, 39.29],
    financing: 'Government & DPs',
    budget: 1010,
    disbursed: 830,
    physical: 91,
    financial: 82,
    status: 'ON_TRACK',
    start: '2017-06-01',
    end: '2026-12-31',
    milestones: [
      { name: 'Berths 1–7 deepening and strengthening', due: '2023-12-31', done: true },
      { name: 'Ro-Ro berth', due: '2024-12-31', done: true },
      { name: 'Berths 8–11 upgrade', due: '2026-12-31', done: false },
    ],
  },
  {
    id: 'lake-victoria-tabora',
    name: 'Lake Victoria Water Supply to Tabora, Nzega & Igunga',
    shortName: 'LV–Tabora Water',
    category: 'water',
    implementer: 'Ministry of Water',
    location: 'Tabora',
    contractor: 'Multiple contractors',
    coords: [-5.02, 32.81],
    financing: 'Government & DPs',
    budget: 620,
    disbursed: 620,
    physical: 100,
    financial: 100,
    status: 'COMPLETED',
    start: '2018-08-01',
    end: '2023-06-30',
    milestones: [
      { name: 'Intake and treatment plant', due: '2021-06-30', done: true },
      { name: 'Transmission main to Tabora', due: '2022-06-30', done: true },
      { name: 'Distribution networks', due: '2023-06-30', done: true },
    ],
  },
  {
    id: 'same-mwanga',
    name: 'Same – Mwanga – Korogwe Water Project',
    shortName: 'Same–Mwanga',
    category: 'water',
    implementer: 'Ministry of Water',
    location: 'Kilimanjaro & Tanga',
    contractor: 'Multiple contractors',
    coords: [-4.07, 37.73],
    financing: 'Government & DPs',
    budget: 410,
    disbursed: 300,
    physical: 79,
    financial: 73,
    status: 'DELAYED',
    start: '2019-01-15',
    end: '2025-12-31',
    milestones: [
      { name: 'Treatment plant', due: '2023-12-31', done: true },
      { name: 'Transmission to Mwanga', due: '2024-12-31', done: true },
      { name: 'Transmission to Korogwe', due: '2025-12-31', done: false },
    ],
  },
  {
    id: 'bbt',
    name: 'Building a Better Tomorrow – Youth Agribusiness',
    shortName: 'BBT',
    category: 'agriculture',
    implementer: 'Ministry of Agriculture',
    location: 'Dodoma, Mbeya & Kagera blocks',
    contractor: 'Force account',
    coords: [-6.16, 35.75],
    financing: 'Government',
    budget: 300,
    disbursed: 140,
    physical: 44,
    financial: 47,
    status: 'AT_RISK',
    start: '2022-07-01',
    end: '2027-06-30',
    milestones: [
      { name: 'Youth cohort training', due: '2023-06-30', done: true },
      { name: 'Block farm clearing and irrigation', due: '2025-06-30', done: false },
      { name: 'Settlement of the second cohort', due: '2026-06-30', done: false },
    ],
  },
  {
    id: 'irrigation',
    name: 'Large-scale Irrigation Schemes',
    shortName: 'Irrigation',
    category: 'agriculture',
    implementer: 'National Irrigation Commission',
    location: 'Nationwide',
    contractor: 'Multiple contractors',
    coords: [-7.77, 35.69],
    financing: 'Government',
    budget: 920,
    disbursed: 520,
    physical: 57,
    financial: 57,
    status: 'ON_TRACK',
    start: '2022-07-01',
    end: '2027-06-30',
    milestones: [
      { name: 'Feasibility studies for priority schemes', due: '2023-06-30', done: true },
      { name: 'Construction of the first 20 schemes', due: '2025-06-30', done: true },
      { name: 'Handover to farmer organisations', due: '2027-06-30', done: false },
    ],
  },
  {
    id: 'referral-hospitals',
    name: 'Regional Referral Hospitals Expansion',
    shortName: 'RRH Expansion',
    category: 'health',
    implementer: 'Ministry of Health',
    location: 'Multiple regions',
    contractor: 'Force account',
    coords: [-6.16, 35.75],
    financing: 'Government',
    budget: 450,
    disbursed: 300,
    physical: 69,
    financial: 67,
    status: 'ON_TRACK',
    start: '2021-07-01',
    end: '2026-12-31',
    milestones: [
      { name: 'Designs for all sites', due: '2022-06-30', done: true },
      { name: 'Outpatient blocks', due: '2024-12-31', done: true },
      { name: 'Specialised units and equipment', due: '2026-12-31', done: false },
    ],
  },
];

const INSTITUTION_NAMES: Record<string, string> = {
  TANESCO: 'Tanzania Electric Supply Company Limited',
  TRC: 'Tanzania Railways Corporation',
  TPA: 'Tanzania Ports Authority',
  TANROADS: 'Tanzania National Roads Agency',
  'Rural Energy Agency': 'Rural Energy Agency',
  'EACOP Ltd / TPDC': 'Tanzania Petroleum Development Corporation',
  TPDC: 'Tanzania Petroleum Development Corporation',
};

/** Same day n months later, kept within the month (31 Dec + 6 → 30 Jun). */
function addMonths(date: string, months: number): string {
  const d = new Date(date);
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + months);
  const lastDay = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, lastDay));
  return d.toISOString().slice(0, 10);
}

const SEED_TODAY = '2026-10-22';

/**
 * Work packages from the milestones, plus supervision across the whole contract (to the
 * extended completion date, if any). A project in preparation has spent nothing on works
 * yet — its preparation costs sit under project management.
 */
function seedActivities(seed: ProjectSeed, finish: string): ProjectActivity[] {
  const packageShare = (seed.budget * 0.95) / seed.milestones.length;
  const preparing = seed.status === 'PREPARATION';
  let openIndex = 0;

  const packages = seed.milestones.map((m, k): ProjectActivity => {
    const start = k === 0 ? seed.start : seed.milestones[k - 1].due;
    let completion: number;
    let status: ActivityStatus;
    if (m.done) {
      completion = 100;
      status = 'COMPLETED';
    } else if (start > SEED_TODAY) {
      completion = 0;
      status = 'PENDING';
    } else {
      completion = preparing
        ? Math.max(0, seed.physical * 2 - openIndex * 10)
        : Math.max(5, Math.round(seed.physical - openIndex * 30));
      status = m.due < SEED_TODAY ? 'DELAYED' : 'IN_PROGRESS';
      openIndex++;
    }
    const budget = Math.round(packageShare * 10) / 10;
    const done = preparing ? 0 : completion / 100;
    return {
      id: `${seed.id}-a${k + 1}`,
      title: m.name,
      description: `Work package ${k + 1} of ${seed.shortName}, delivered by ${seed.contractor}.`,
      start,
      end: m.due,
      budget,
      disbursed: Math.round(Math.min(budget, budget * done * 1.05) * 10) / 10,
      spent: Math.round(budget * done * 0.95 * 10) / 10,
      completion,
      previousCompletion: completion >= 100 ? 100 : Math.max(0, completion - 8),
      status,
      reporter: k === 0 ? `${seed.implementer} site engineer` : null,
      subActivities: ['Mobilisation and site preparation', 'Main works', 'Testing and handover'].map(
        (title, i) => ({ id: `${seed.id}-a${k + 1}-s${i + 1}`, title, done: completion >= (i + 1) * 33 }),
      ),
    };
  });

  const supervisionBudget = Math.round(seed.budget * 0.05 * 10) / 10;
  const supervisionDisbursed = preparing
    ? seed.disbursed
    : Math.round(supervisionBudget * (seed.financial / 100) * 10) / 10;
  const supervision: ProjectActivity = {
    id: `${seed.id}-a0`,
    title: 'Supervision and project management',
    description: `Consultancy supervision and ${seed.implementer} project management for the whole contract.`,
    start: seed.start,
    end: finish,
    budget: supervisionBudget,
    disbursed: supervisionDisbursed,
    spent: Math.round(supervisionDisbursed * 0.95 * 10) / 10,
    completion: seed.physical,
    previousCompletion: Math.max(0, seed.physical - 5),
    status:
      seed.status === 'COMPLETED'
        ? 'COMPLETED'
        : seed.status === 'DELAYED' || finish < SEED_TODAY
          ? 'DELAYED'
          : 'IN_PROGRESS',
    reporter: null,
    subActivities: [],
  };

  return [...packages, supervision];
}

function seedProject(seed: ProjectSeed): FlagshipProject {
  const { milestones, ...project } = seed;
  const extensions = seed.status === 'DELAYED' ? [addMonths(seed.end, 6)] : [];
  const activities = seedActivities(seed, extensions.at(-1) ?? seed.end);
  const [lat, lng] = seed.coords;
  return {
    ...project,
    contractSigned: addMonths(seed.start, -6),
    extensions,
    institutions: [
      {
        shortName: seed.implementer,
        name: INSTITUTION_NAMES[seed.implementer] ?? seed.implementer,
        role: 'Implementing agency',
      },
      { shortName: 'MoF', name: 'Ministry of Finance', role: 'Financing and oversight' },
    ],
    activities,
    evidence: milestones
      .filter((m) => m.done)
      .slice(-2)
      .map((m, i) => ({
        id: `${seed.id}-e${i + 1}`,
        caption: `${m.name} — site inspection`,
        activityId: activities.find((a) => a.title === m.name)?.id ?? null,
        date: m.due,
        lat: Math.round((lat + i * 0.012) * 10000) / 10000,
        lng: Math.round((lng - i * 0.009) * 10000) / 10000,
        photo: null,
      })),
  };
}

export const FLAGSHIP_PROJECTS: FlagshipProject[] = PROJECT_SEEDS.map(seedProject);

export const PROJECT_CATEGORIES: { key: string; name: string; icon: string }[] = [
  { key: 'energy', name: 'Energy', icon: 'bolt' },
  { key: 'transport', name: 'Transport & Infrastructure', icon: 'train' },
  { key: 'water', name: 'Water', icon: 'water_drop' },
  { key: 'agriculture', name: 'Agriculture', icon: 'agriculture' },
  { key: 'health', name: 'Health', icon: 'local_hospital' },
];

// ── The frameworks ──────────────────────────────────────────────────────────────

const VISION: FrameworkDefinition = {
  key: 'vision',
  tier: 1,
  title: 'Development Vision',
  subtitle: 'Vision 2026 – 2050',
  shortName: 'Vision 2050',
  name: 'Tanzania Development Vision 2050',
  period: '2026 – 2050',
  icon: 'flag',
  description:
    'The long-term national destination. Every plan, manifesto and strategic plan below it is judged by how far it moves these pillars.',
  implements: [],
  reviews: { scheduled: 104, conducted: 97, directives: 112, implemented: 58 },
  processes: [
    {
      key: 'dashboard',
      name: 'Vision Dashboard',
      icon: 'space_dashboard',
      kind: 'overview',
      group: 'Overview',
      summary: 'Performance across the Vision pillars and the review meetings that follow them up.',
    },
    {
      key: 'livelihood',
      name: 'Quality Livelihood',
      icon: 'diversity_3',
      kind: 'area',
      group: 'Pillars',
      summary: 'A high-quality livelihood for all, free of abject poverty, with urban–rural gaps closing.',
      indicators: [
        ind('vl1', 'Life expectancy at birth', 'years', 'Ministry of Health', 66, 2022, 69, 75, 2050, 67.4),
        ind('vl2', 'Population below the basic-needs poverty line', '%', 'Ministry of Finance', 26.4, 2018, 18, 5, 2050, 23.1),
        ind('vl3', 'Households with access to clean and safe water', '%', 'Ministry of Water', 72, 2022, 90, 100, 2050, 79),
        ind('vl4', 'Households connected to electricity', '%', 'Ministry of Energy', 37, 2022, 60, 95, 2050, 46),
      ],
      regions: [
        { name: 'Dar es Salaam', score: 74 },
        { name: 'Arusha', score: 69 },
        { name: 'Kilimanjaro', score: 68 },
        { name: 'Urban West', score: 66 },
        { name: 'Dodoma', score: 63 },
        { name: 'Mwanza', score: 61 },
        { name: 'Iringa', score: 60 },
        { name: 'Mbeya', score: 59 },
        { name: 'Njombe', score: 58 },
        { name: 'Pwani', score: 57 },
        { name: 'Morogoro', score: 56 },
        { name: 'Ruvuma', score: 55 },
        { name: 'Tanga', score: 54 },
        { name: 'Mtwara', score: 52 },
        { name: 'Manyara', score: 51 },
        { name: 'South Unguja', score: 50 },
        { name: 'Songwe', score: 49 },
        { name: 'Lindi', score: 48 },
        { name: 'North Unguja', score: 47 },
        { name: 'Mara', score: 46 },
        { name: 'Shinyanga', score: 45 },
        { name: 'Geita', score: 44 },
        { name: 'Singida', score: 43 },
        { name: 'Rukwa', score: 42 },
        { name: 'Kagera', score: 41 },
        { name: 'Tabora', score: 40 },
        { name: 'South Pemba', score: 39 },
        { name: 'Katavi', score: 38 },
        { name: 'Simiyu', score: 37 },
        { name: 'North Pemba', score: 36 },
        { name: 'Kigoma', score: 35 },
      ],
    },
    {
      key: 'peace',
      name: 'Peace, Stability & Unity',
      icon: 'handshake',
      kind: 'area',
      group: 'Pillars',
      summary: 'A peaceful, stable and united nation where people feel safe and disputes are settled fairly.',
      indicators: [
        ind('vp1', 'Citizens who feel safe walking in their area at night', '%', "Ministry of Home Affairs", 68, 2022, 75, 90, 2050, 72),
        ind('vp2', 'Reported violent crime incidents', 'per 100,000', 'Ministry of Home Affairs', 120, 2022, 100, 60, 2050, 111),
        ind('vp3', 'Land disputes resolved through reconciliation', '%', 'Ministry of Lands', 40, 2022, 55, 80, 2050, 46),
      ],
    },
    {
      key: 'governance',
      name: 'Good Governance',
      icon: 'gavel',
      kind: 'area',
      group: 'Pillars',
      summary: 'Accountable, transparent and responsive institutions under the rule of law.',
      indicators: [
        ind('vg1', 'Public institutions with clean audit opinions', '%', 'Controller and Auditor General', 82, 2022, 88, 98, 2050, 86),
        ind('vg2', 'Government services available online', '%', 'e-Government Authority', 35, 2022, 60, 95, 2050, 52),
        ind('vg3', 'Corruption cases concluded within 12 months', '%', 'PCCB', 45, 2022, 60, 85, 2050, 49),
      ],
    },
    {
      key: 'education',
      name: 'Well-Educated & Learning Society',
      icon: 'school',
      kind: 'area',
      group: 'Pillars',
      summary: 'A society that values knowledge, with quality education from early years to tertiary level.',
      indicators: [
        ind('ve1', 'Net enrolment rate — primary', '%', 'Ministry of Education', 86, 2022, 92, 100, 2050, 89),
        ind('ve2', 'Transition rate from primary to secondary', '%', 'Ministry of Education', 73, 2022, 85, 98, 2050, 80),
        ind('ve3', 'Adult literacy rate', '%', 'Ministry of Education', 83, 2022, 88, 98, 2050, 84),
      ],
    },
    {
      key: 'economy',
      name: 'Competitive Economy',
      icon: 'trending_up',
      kind: 'area',
      group: 'Pillars',
      summary: 'A diversified, semi-industrialised economy able to withstand regional and global competition.',
      indicators: [
        ind('vc1', 'Real GDP growth rate', '%', 'Ministry of Finance', 4.7, 2022, 7, 8, 2050, 5.5),
        ind('vc2', 'Manufacturing share of GDP', '%', 'Ministry of Industry and Trade', 8, 2022, 12, 20, 2050, 8.6),
        ind('vc3', 'GDP per capita', 'USD', 'Ministry of Finance', 1200, 2022, 2000, 7000, 2050, 1280),
        ind('vc4', 'Installed electricity generation capacity', 'MW', 'Ministry of Energy', 1700, 2022, 5000, 15000, 2050, 3800),
      ],
    },
  ],
};

const FYDP: FrameworkDefinition = {
  key: 'fydp',
  tier: 2,
  title: "FYDP's",
  subtitle: 'NFYDP for 2021/22 – 2025/26',
  shortName: 'FYDP III',
  name: 'Third National Five Year Development Plan',
  period: '2021/22 – 2025/26',
  icon: 'event_note',
  description:
    'Turns the Vision into five-year priority areas, interventions and budgets, with an implementation matrix tracked every year.',
  implements: ['vision'],
  processes: [
    {
      key: 'dashboard',
      name: 'FYDP Dashboard',
      icon: 'space_dashboard',
      kind: 'overview',
      group: 'Overview',
      summary: 'Progress on the five priority areas and the sector spotlight.',
    },
    {
      key: 'interventions',
      name: 'Implementation Matrix',
      icon: 'table_view',
      kind: 'matrix',
      group: 'Delivery',
      summary: 'Priority interventions with their targets, programmes, financing and progress.',
      matrixAreas: [
        { name: 'Competitiveness Enhancement', summary: 'Measures that make the country more competitive.' },
        { name: 'Industrialisation & Services', summary: 'Deepening industrial development and services.' },
        { name: 'Investment & Trade', summary: 'Promoting participation in global and regional trade.' },
        { name: 'Human Development', summary: 'Equitable access to quality social services for inclusive growth.' },
        { name: 'Skills Development', summary: 'Enhancing the skills and capabilities of the workforce.' },
      ],
      interventions: [
        {
          id: 'i1',
          implementer: 'TANESCO',
          area: 'Competitiveness Enhancement',
          intervention: 'Increase electricity generation, transmission and distribution capacity',
          target: 'Generation capacity raised by 5,760 MW',
          project: 'Julius Nyerere Hydropower Project (2,115 MW)',
          government: 5817.7,
          dps: 0,
          privateSector: 0,
          expectedOutput: 'JNHPP commissioned',
          financial: 92,
          physical: 96,
        },
        {
          id: 'i2',
          implementer: 'TANESCO',
          area: 'Competitiveness Enhancement',
          intervention: 'Increase electricity generation, transmission and distribution capacity',
          target: 'Generation capacity raised by 5,760 MW',
          project: 'Kinyerezi I Extension (185 MW)',
          government: 60,
          dps: 0,
          privateSector: 0,
          expectedOutput: 'Plant extension completed',
          financial: 100,
          physical: 100,
        },
        {
          id: 'i2b',
          implementer: 'Rusumo Power Company',
          area: 'Competitiveness Enhancement',
          intervention: 'Increase electricity generation, transmission and distribution capacity',
          target: 'Generation capacity raised by 5,760 MW',
          project: 'Rusumo Hydropower Project (80 MW), shared equally by Tanzania, Burundi and Rwanda',
          government: 496.2,
          dps: 0,
          privateSector: 0,
          expectedOutput: 'Rusumo plant completed',
          financial: 100,
          physical: 100,
        },
        {
          id: 'i3',
          implementer: 'TANESCO',
          area: 'Competitiveness Enhancement',
          intervention: 'Increase electricity generation, transmission and distribution capacity',
          target: 'Generation capacity raised by 5,760 MW',
          project: 'Ruhudji Hydropower Project (358 MW)',
          government: 965.3,
          dps: 0,
          privateSector: 0,
          expectedOutput: 'Ruhudji plant completed',
          financial: 18,
          physical: 12,
        },
        {
          id: 'i4',
          implementer: 'TRC',
          area: 'Competitiveness Enhancement',
          intervention: 'Modernise rail transport',
          target: 'SGR Dar es Salaam – Mwanza operational',
          project: 'Standard Gauge Railway Lots 1–5',
          government: 10500,
          dps: 4200,
          privateSector: 0,
          expectedOutput: 'Passenger and cargo services to Mwanza',
          financial: 66,
          physical: 78,
        },
        {
          id: 'i5',
          implementer: 'Ministry of Industry and Trade',
          area: 'Industrialisation & Services',
          intervention: 'Expand agro-processing capacity',
          target: 'Value addition of key crops increased',
          project: 'Agro-industrial parks',
          government: 120,
          dps: 30,
          privateSector: 450,
          expectedOutput: 'Three agro-industrial parks operating',
          financial: 41,
          physical: 38,
        },
        {
          id: 'i6',
          implementer: 'TPA',
          area: 'Investment & Trade',
          intervention: 'Improve port efficiency',
          target: 'Cargo handled raised to 25 million tonnes',
          project: 'Dar es Salaam Port modernisation',
          government: 400,
          dps: 610,
          privateSector: 0,
          expectedOutput: 'Deepened and strengthened berths',
          financial: 82,
          physical: 91,
        },
        {
          id: 'i7',
          implementer: 'PO-RALG',
          area: 'Human Development',
          intervention: 'Expand access to quality health services',
          target: 'Health centre in every ward',
          project: 'Construction of health centres',
          government: 300,
          dps: 45,
          privateSector: 0,
          expectedOutput: '600 health centres operational',
          financial: 62,
          physical: 58,
        },
        {
          id: 'i8',
          implementer: 'VETA',
          area: 'Skills Development',
          intervention: 'Expand technical and vocational training',
          target: 'VET college in every district',
          project: 'District VETA colleges',
          government: 200,
          dps: 50,
          privateSector: 0,
          expectedOutput: '64 district colleges completed',
          financial: 55,
          physical: 49,
        },
      ],
    },
    {
      key: 'competitiveness',
      name: 'Competitiveness Enhancement',
      icon: 'speed',
      kind: 'area',
      group: 'Priority areas',
      summary: 'Measures that make the economy more competitive — energy, transport and logistics.',
      indicators: [
        ind('fc1', 'Installed electricity generation capacity', 'MW', 'Ministry of Energy', 1602, 2020, 3000, 4900, 2026, 3800),
        ind('fc2', 'Trunk roads paved', '%', 'TANROADS', 62, 2020, 70, 85, 2026, 74),
        ind('fc3', 'Average ship turnaround time at Dar es Salaam port', 'days', 'TPA', 7, 2020, 5, 3, 2026, 5),
      ],
    },
    {
      key: 'industrialisation',
      name: 'Industrialisation & Services',
      icon: 'factory',
      kind: 'area',
      group: 'Priority areas',
      summary: 'Deepening industrial development and expanding services such as tourism.',
      indicators: [
        ind('fi1', 'Manufacturing share of GDP', '%', 'Ministry of Industry and Trade', 8.5, 2020, 9.5, 10.5, 2026, 8.9),
        ind('fi2', 'Manufactured exports', 'USD million', 'Ministry of Industry and Trade', 1100, 2020, 1800, 2500, 2026, 1520),
        ind('fi3', 'International tourist arrivals', 'million', 'Ministry of Natural Resources and Tourism', 1.5, 2020, 3, 5, 2026, 2.1),
      ],
    },
    {
      key: 'investment-trade',
      name: 'Investment & Trade',
      icon: 'swap_horiz',
      kind: 'area',
      group: 'Priority areas',
      summary: 'Promoting investment and participation in regional and global trade.',
      indicators: [
        ind('ft1', 'Foreign direct investment inflows', 'USD million', 'Tanzania Investment Centre', 1000, 2020, 1600, 2500, 2026, 1340),
        ind('ft2', 'Goods exports', 'USD billion', 'Ministry of Industry and Trade', 4.9, 2020, 7, 10, 2026, 7.4),
        ind('ft3', 'New investment projects registered', 'per year', 'Tanzania Investment Centre', 200, 2020, 350, 500, 2026, 470),
      ],
    },
    {
      key: 'human-development',
      name: 'Human Development',
      icon: 'health_and_safety',
      kind: 'area',
      group: 'Priority areas',
      summary: 'Equitable access to quality social services for inclusive growth.',
      indicators: [
        ind('fh1', 'Households within 5 km of a health facility', '%', 'Ministry of Health', 70, 2020, 80, 90, 2026, 81),
        ind('fh2', 'Rural population with access to clean water', '%', 'RUWASA', 70, 2020, 80, 85, 2026, 79),
        ind('fh3', 'Urban population with access to clean water', '%', 'Ministry of Water', 84, 2020, 90, 95, 2026, 90),
      ],
    },
    {
      key: 'skills',
      name: 'Skills Development',
      icon: 'engineering',
      kind: 'area',
      group: 'Priority areas',
      summary: 'Building the workforce skills that industrialisation needs.',
      indicators: [
        ind('fs1', 'Enrolment in vocational training', 'thousand', 'VETA', 200, 2020, 350, 700, 2026, 420),
        ind('fs2', 'Graduates in science and engineering', '% of graduates', 'Ministry of Education', 20, 2020, 25, 35, 2026, 24),
        ind('fs3', 'Youth in training or employment', '%', "Prime Minister's Office (Labour)", 55, 2020, 62, 70, 2026, 59),
      ],
    },
    {
      key: 'agriculture',
      name: 'Agricultural Improvement',
      icon: 'agriculture',
      kind: 'area',
      group: 'Sector spotlight',
      summary: 'Irrigation, productivity and youth participation in agriculture.',
      indicators: [
        ind('fa1', 'Area under irrigation', 'hectares', 'National Irrigation Commission', 695000, 2020, 900000, 1200000, 2026, 980000),
        ind('fa2', 'Agriculture sector growth', '%', 'Ministry of Agriculture', 4.4, 2020, 6, 10, 2026, 4.2),
        ind('fa3', 'Youth enrolled in BBT', 'people', 'Ministry of Agriculture', 0, 2022, 5000, 12000, 2026, 7200),
        ind('fa4', 'Food self-sufficiency ratio', '%', 'Ministry of Agriculture', 114, 2020, 120, 130, 2026, 124),
      ],
    },
  ],
};

const MANIFESTO: FrameworkDefinition = {
  key: 'manifesto',
  tier: 2,
  title: 'Election Manifestos',
  subtitle: 'Political Parties and their Manifestos',
  shortName: 'Manifesto',
  name: 'CCM Election Manifesto 2025 – 2030',
  period: '2025 – 2030',
  icon: 'campaign',
  description:
    "The ruling party's commitments for its term, tracked chapter by chapter and followed up through review meetings and directives.",
  implements: ['vision'],
  reviews: { scheduled: 138, conducted: 97, directives: 112, implemented: 58 },
  processes: [
    {
      key: 'dashboard',
      name: 'Manifesto Dashboard',
      icon: 'space_dashboard',
      kind: 'overview',
      group: 'Overview',
      summary: 'Delivery of manifesto commitments across all chapters.',
    },
    {
      key: 'economy',
      name: 'Economic Revolution',
      icon: 'payments',
      kind: 'area',
      group: 'Chapters',
      summary: 'Jobs, growth and domestic revenue.',
      indicators: [
        ind('me1', 'Jobs created', 'million', "Prime Minister's Office (Labour)", 0, 2025, 4, 8, 2030, 1.9),
        ind('me2', 'Domestic revenue collection', 'TZS trillion', 'Tanzania Revenue Authority', 31, 2025, 38, 45, 2030, 34),
        ind('me3', 'Average annual GDP growth', '%', 'Ministry of Finance', 5.5, 2025, 6.5, 7, 2030, 5.8),
      ],
    },
    {
      key: 'social',
      name: 'Social Services',
      icon: 'medical_services',
      kind: 'area',
      group: 'Chapters',
      summary: 'Education, health and water for every household.',
      indicators: [
        ind('ms1', 'Pupils in fee-free basic education', 'million', 'Ministry of Education', 14, 2025, 15.5, 17, 2030, 14.6),
        ind('ms2', 'New health centres built', 'count', 'PO-RALG', 0, 2025, 300, 600, 2030, 140),
        ind('ms3', 'Rural water access', '%', 'RUWASA', 79, 2025, 85, 90, 2030, 81),
      ],
    },
    {
      key: 'sti',
      name: 'Science, Technology & Innovation',
      icon: 'science',
      kind: 'area',
      group: 'Chapters',
      summary: 'Connectivity, digital services and support for innovators.',
      indicators: [
        ind('mt1', 'Internet penetration', '%', 'TCRA', 49, 2025, 65, 80, 2030, 55),
        ind('mt2', 'Wards with 4G coverage', '%', 'UCSAF', 60, 2025, 80, 95, 2030, 70),
        ind('mt3', 'Innovators supported', 'count', 'COSTECH', 500, 2025, 1500, 3000, 2030, 900),
      ],
    },
    {
      key: 'security',
      name: 'Security & Safety',
      icon: 'shield',
      kind: 'area',
      group: 'Chapters',
      summary: 'Safety of people and property.',
      indicators: [
        ind('mx1', 'Wards with a police post', '%', 'Tanzania Police Force', 40, 2025, 60, 80, 2030, 46),
        ind('mx2', 'Road traffic deaths', 'per year', 'Tanzania Police Force', 1400, 2025, 1200, 900, 2030, 1330),
        ind('mx3', 'District HQs with fire and rescue services', '%', 'Fire and Rescue Force', 55, 2025, 75, 100, 2030, 61),
      ],
    },
    {
      key: 'governance',
      name: 'Good Governance',
      icon: 'balance',
      kind: 'area',
      group: 'Chapters',
      summary: 'Responsive, accountable public service.',
      indicators: [
        ind('mg1', 'Public complaints resolved within 30 days', '%', 'PO-PSMGG', 60, 2025, 75, 90, 2030, 69),
        ind('mg2', 'Land parcels surveyed and titled', 'million', 'Ministry of Lands', 2.5, 2025, 4, 6, 2030, 3.1),
        ind('mg3', 'Councils with clean audit opinions', '%', 'PO-RALG', 78, 2025, 85, 95, 2030, 82),
      ],
    },
    {
      key: 'foreign',
      name: 'Foreign Affairs',
      icon: 'public',
      kind: 'area',
      group: 'Chapters',
      summary: 'Economic diplomacy and the diaspora.',
      indicators: [
        ind('mf1', 'Investment pledges realised through economic diplomacy', 'USD billion', 'Ministry of Foreign Affairs', 0, 2025, 3, 6, 2030, 1.4),
        ind('mf2', 'Diaspora members registered', 'thousand', 'Ministry of Foreign Affairs', 50, 2025, 150, 300, 2030, 96),
        ind('mf3', 'Bilateral agreements operationalised', 'count', 'Ministry of Foreign Affairs', 0, 2025, 20, 40, 2030, 9),
      ],
    },
    {
      key: 'zanzibar',
      name: 'Zanzibar',
      icon: 'sailing',
      kind: 'area',
      group: 'Chapters',
      summary: 'Commitments specific to Zanzibar, including the blue economy.',
      indicators: [
        ind('mz1', 'Blue economy share of Zanzibar GDP', '%', 'Revolutionary Government of Zanzibar', 25, 2025, 30, 35, 2030, 27),
        ind('mz2', 'Tourist arrivals in Zanzibar', 'thousand', 'Zanzibar Commission for Tourism', 550, 2025, 800, 1000, 2030, 690),
      ],
    },
    {
      key: 'other',
      name: 'Other Priority Areas',
      icon: 'more_horiz',
      kind: 'area',
      group: 'Chapters',
      summary: 'Youth, women and people with disabilities.',
      indicators: [
        ind('mo1', 'Youth empowerment loans disbursed', 'TZS billion', 'PO-RALG', 0, 2025, 100, 200, 2030, 52),
        ind('mo2', 'Women-led enterprises formalised', 'thousand', 'Ministry of Community Development', 20, 2025, 60, 120, 2030, 38),
        ind('mo3', 'People with disabilities reached by support schemes', 'thousand', "Prime Minister's Office", 10, 2025, 30, 60, 2030, 18),
      ],
    },
  ],
};

const STRATEGIC_PLANS: FrameworkDefinition = {
  key: 'strategic-plans',
  tier: 3,
  title: "MDA's & LGA's SP's",
  subtitle: 'Institutional Strategic Plans (SPs)',
  shortName: 'Strategic Plans',
  name: 'MDA & LGA Strategic Plans',
  period: 'Five-year rolling plans',
  icon: 'corporate_fare',
  description:
    'Each ministry, department, agency and local government authority delivers the frameworks above through its own strategic plan, run through the same M&E cycle.',
  implements: ['fydp', 'manifesto', 'international'],
  processes: [
    {
      key: 'dashboard',
      name: 'SP Dashboard',
      icon: 'space_dashboard',
      kind: 'cycle',
      group: 'Overview',
      summary: 'Where every institution is in the strategic plan M&E cycle.',
    },
    {
      key: 'goals',
      name: 'Goals, Objectives, Targets & Indicators',
      icon: 'track_changes',
      kind: 'cycle',
      group: 'M&E cycle',
      summary: 'Results framework for the plan.',
      step: {
        purpose: 'Break the plan into strategic objectives, measurable targets and the indicators that will track them.',
        frequency: 'Once per plan, reviewed annually',
        lead: 'Planning unit',
        activities: [
          "Derive objectives from the institution's mandate and the national frameworks",
          'Set SMART targets for each objective',
          'Attach at least one indicator to every target',
        ],
        outputs: ['Results framework matrix'],
      },
    },
    {
      key: 'kpis',
      name: "Define KPI's & Metrics",
      icon: 'straighten',
      kind: 'cycle',
      group: 'M&E cycle',
      summary: 'Indicator definitions, baselines and annual targets.',
      step: {
        purpose: 'Define each indicator precisely so everyone measures it the same way.',
        frequency: 'Once per plan',
        lead: 'M&E unit',
        activities: [
          'Write an indicator reference sheet — definition, formula, unit',
          'Set baselines and annual targets',
          'Name the data source and collection frequency',
        ],
        outputs: ['Indicator reference sheets', 'Baseline report'],
      },
    },
    {
      key: 'alignment',
      name: 'Linkage & Alignments',
      icon: 'link',
      kind: 'cycle',
      group: 'M&E cycle',
      summary: 'How each objective serves the national frameworks.',
      step: {
        purpose:
          'Map every objective to the Vision pillar, FYDP priority area, manifesto chapter and SDG or Agenda 2063 goal it serves.',
        frequency: 'Once per plan, refreshed when a national framework changes',
        lead: 'Planning unit',
        activities: [
          'Map objectives to national framework areas',
          'Flag objectives with no national linkage',
          'Confirm linkages with the Planning Commission',
        ],
        outputs: ['Alignment matrix'],
      },
    },
    {
      key: 'reporting',
      name: 'Reporting & Data Inputs',
      icon: 'upload_file',
      kind: 'cycle',
      group: 'M&E cycle',
      summary: 'Quarterly collection and submission of actual values.',
      step: {
        purpose: 'Collect actual values for every indicator on schedule and submit them through the system.',
        frequency: 'Quarterly',
        lead: 'M&E unit with departments',
        activities: [
          'Collect data from departments and units',
          'Validate values against source documents',
          'Submit the quarterly return',
        ],
        outputs: ['Quarterly progress report'],
      },
    },
    {
      key: 'analysis',
      name: 'Performance Analysis',
      icon: 'analytics',
      kind: 'cycle',
      group: 'M&E cycle',
      summary: 'Actual against target, with variances explained.',
      step: {
        purpose: 'Compare actual values with targets and explain the variances.',
        frequency: 'Quarterly and annually',
        lead: 'M&E unit',
        activities: [
          'Compute achievement against annual targets',
          'Explain significant variances',
          'Rank objectives by performance',
        ],
        outputs: ['Annual performance report'],
      },
    },
    {
      key: 'tracking',
      name: 'Progress & Outcomes Tracking',
      icon: 'timeline',
      kind: 'cycle',
      group: 'M&E cycle',
      summary: 'Whether outputs are turning into outcomes.',
      step: {
        purpose: 'Follow whether outputs are turning into the outcomes the plan promised.',
        frequency: 'Semi-annually',
        lead: 'M&E unit',
        activities: [
          'Track outcome indicators over time',
          'Hold mid-year performance reviews',
          'Record evidence of outcome change',
        ],
        outputs: ['Mid-year review report'],
      },
    },
    {
      key: 'feedback',
      name: 'Feedback Mechanism',
      icon: 'forum',
      kind: 'cycle',
      group: 'M&E cycle',
      summary: 'Feedback from citizens, staff and stakeholders.',
      step: {
        purpose: 'Gather feedback from citizens, staff and stakeholders on how services are delivered.',
        frequency: 'Continuous',
        lead: 'Public relations unit',
        activities: [
          'Run client service surveys',
          'Log and resolve complaints',
          'Report feedback themes to management',
        ],
        outputs: ['Client satisfaction survey', 'Complaints register'],
      },
    },
    {
      key: 'adaptation',
      name: 'SP Adaptation Measures',
      icon: 'tune',
      kind: 'cycle',
      group: 'M&E cycle',
      summary: 'Corrective action when the plan drifts.',
      step: {
        purpose: 'Adjust targets, strategies or resources when reviews show the plan is off course.',
        frequency: 'Annually',
        lead: 'Management',
        activities: [
          'Agree corrective actions after each review',
          'Revise targets with written justification',
          'Reallocate resources in the next budget',
        ],
        outputs: ['Approved plan revisions'],
      },
    },
    {
      key: 'accountability',
      name: 'Accountability Strategy',
      icon: 'verified_user',
      kind: 'cycle',
      group: 'M&E cycle',
      summary: 'Who answers for each result.',
      step: {
        purpose: 'Make clear who answers for each result and publish performance openly.',
        frequency: 'Annually',
        lead: 'Accounting Officer',
        activities: [
          'Assign a result owner to each objective',
          'Sign performance agreements',
          'Publish the annual performance report',
        ],
        outputs: ['Performance agreements', 'Published annual report'],
      },
    },
    {
      key: 'benchmarking',
      name: 'SP Benchmarking',
      icon: 'compare_arrows',
      kind: 'cycle',
      group: 'M&E cycle',
      summary: 'Comparison with peers and good practice.',
      step: {
        purpose: 'Compare performance with peer institutions and good practice.',
        frequency: 'Annually',
        lead: 'Planning unit',
        activities: ['Select peer institutions', 'Compare common indicators', 'Adopt practices that work'],
        outputs: ['Benchmarking report'],
      },
    },
    {
      key: 'risk',
      name: 'Risk Management',
      icon: 'warning',
      kind: 'cycle',
      group: 'M&E cycle',
      summary: 'Risks to delivery and how they are treated.',
      step: {
        purpose: 'Identify what could stop the plan delivering and how each risk will be handled.',
        frequency: 'Quarterly',
        lead: 'Risk management unit',
        activities: ['Maintain the risk register', 'Rate likelihood and impact', 'Track mitigation actions'],
        outputs: ['Risk register', 'Risk treatment plan'],
      },
    },
    {
      key: 'quality',
      name: 'Quality Assurance',
      icon: 'fact_check',
      kind: 'cycle',
      group: 'M&E cycle',
      summary: 'Accuracy, completeness and timeliness of data.',
      step: {
        purpose: 'Check that reported data is accurate, complete and on time.',
        frequency: 'Quarterly',
        lead: 'Internal audit unit',
        activities: [
          'Spot-check reported values against evidence',
          'Run data quality assessments',
          'Record and close data issues',
        ],
        outputs: ['Data quality assessment report'],
      },
    },
    {
      key: 'effectiveness',
      name: 'Evaluate SP Effectiveness',
      icon: 'insights',
      kind: 'cycle',
      group: 'M&E cycle',
      summary: 'Mid-term and end-of-plan evaluation.',
      step: {
        purpose: 'Evaluate at mid-term and at the end whether the plan achieved its intended results.',
        frequency: 'Mid-term and end of plan',
        lead: 'M&E unit with independent evaluators',
        activities: [
          'Commission the mid-term review',
          'Conduct the end-of-plan evaluation',
          'Feed lessons into the next strategic plan',
        ],
        outputs: ['Mid-term review report', 'Final evaluation report'],
      },
    },
  ],
};

const FLAGSHIP: FrameworkDefinition = {
  key: 'flagship',
  tier: 3,
  title: 'Flagship Projects',
  subtitle: 'Strategic Development Projects',
  shortName: 'Flagship Projects',
  name: 'National Flagship Projects',
  period: 'Multi-year',
  icon: 'engineering',
  description:
    'The large strategic projects that carry the FYDP and manifesto priorities — tracked for physical and financial progress, milestones and risk.',
  implements: ['fydp', 'manifesto'],
  processes: [
    {
      key: 'dashboard',
      name: 'Projects Dashboard',
      icon: 'space_dashboard',
      kind: 'projects',
      group: 'Overview',
      summary: 'The whole flagship portfolio — status, finance and progress.',
    },
    ...PROJECT_CATEGORIES.map(
      (c): FrameworkProcess => ({
        key: c.key,
        name: c.name,
        icon: c.icon,
        kind: 'projects',
        group: 'Sectors',
        summary: `${c.name} flagship projects.`,
        category: c.key,
      }),
    ),
  ],
};

const INTERNATIONAL: FrameworkDefinition = {
  key: 'international',
  tier: 3,
  title: 'International Issues',
  subtitle: "SDG's & Agenda 2063",
  shortName: 'SDGs & Agenda 2063',
  name: 'International Development Commitments',
  period: '2015 – 2030 · 2013 – 2063',
  icon: 'public',
  description:
    'Global and continental agendas Tanzania has committed to — mainstreamed into the national plans and reported on to the UN, AU and treaty bodies.',
  implements: [],
  processes: [
    {
      key: 'dashboard',
      name: 'International Dashboard',
      icon: 'space_dashboard',
      kind: 'overview',
      group: 'Overview',
      summary: 'Agenda 2063 aspirations, the SDGs and convention reporting in one place.',
    },
    {
      key: 'sdg',
      name: 'SDG Tracker',
      icon: 'travel_explore',
      kind: 'link',
      group: 'Global',
      summary: '17 goals, 169 targets and their indicators.',
      route: '/mne/national/frameworks/sdg',
    },
    {
      key: 'sdg-accountability',
      name: 'Ministry Accountability',
      icon: 'groups',
      kind: 'link',
      group: 'Global',
      summary: 'Which ministry answers for each SDG target.',
      route: '/mne/national/frameworks/accountability',
    },
    {
      key: 'conventions',
      name: 'International Conventions',
      icon: 'menu_book',
      kind: 'register',
      group: 'Global',
      summary: 'Treaties Tanzania reports on and when each report is due.',
      items: [
        { id: 'unfccc', shortName: 'UNFCCC', name: 'UN Framework Convention on Climate Change', lead: "Vice President's Office", cycle: 'Biennial transparency report', lastReport: '2024-12-15', nextDue: '2026-12-31', status: 'DUE_SOON' },
        { id: 'cbd', shortName: 'CBD', name: 'Convention on Biological Diversity', lead: "Vice President's Office", cycle: 'National report every 4 years', lastReport: '2026-02-28', nextDue: '2030-02-28', status: 'SUBMITTED' },
        { id: 'iccpr', shortName: 'ICCPR', name: 'International Covenant on Civil and Political Rights', lead: 'Ministry of Constitutional and Legal Affairs', cycle: 'Periodic report', lastReport: '2019-07-01', nextDue: '2025-07-01', status: 'OVERDUE' },
        { id: 'icerd', shortName: 'ICERD', name: 'Convention on the Elimination of Racial Discrimination', lead: 'Ministry of Constitutional and Legal Affairs', cycle: 'Periodic report', lastReport: '2022-03-01', nextDue: '2027-03-01', status: 'ON_TRACK' },
        { id: 'ilo', shortName: 'ILO', name: 'ILO Fundamental Conventions', lead: "Prime Minister's Office (Labour)", cycle: 'Article 22 reports', lastReport: '2025-09-01', nextDue: '2026-09-01', status: 'OVERDUE' },
        { id: 'wto', shortName: 'WTO', name: 'WTO Agreements', lead: 'Ministry of Industry and Trade', cycle: 'Notifications and trade policy review', lastReport: '2025-11-20', nextDue: '2027-11-20', status: 'ON_TRACK' },
        { id: 'unclos', shortName: 'UNCLOS', name: 'UN Convention on the Law of the Sea', lead: 'Ministry of Foreign Affairs', cycle: 'As required', lastReport: '2023-05-10', nextDue: '2027-05-10', status: 'ON_TRACK' },
        { id: 'whc', shortName: 'WHC', name: 'World Heritage Convention', lead: 'Ministry of Natural Resources and Tourism', cycle: 'Periodic report (6-year cycle)', lastReport: '2024-07-31', nextDue: '2030-07-31', status: 'SUBMITTED' },
        { id: 'cwc', shortName: 'CWC', name: 'Chemical Weapons Convention', lead: 'Government Chemist Laboratory Authority', cycle: 'Annual declaration', lastReport: '2026-03-31', nextDue: '2027-03-31', status: 'ON_TRACK' },
        { id: 'geneva', shortName: 'Geneva', name: 'Geneva Conventions', lead: 'Ministry of Defence and National Service', cycle: 'As required', lastReport: '2021-10-01', nextDue: '2026-11-30', status: 'DUE_SOON' },
      ],
    },
    {
      key: 'a1-prosperous',
      name: 'A Prosperous Africa',
      icon: 'savings',
      kind: 'area',
      group: 'Agenda 2063',
      summary: 'Aspiration 1 — inclusive growth and sustainable development.',
      indicators: [
        ind('a11', 'Unemployment rate', '%', 'National Bureau of Statistics', 9.3, 2020, 8, 6, 2030, 8.8),
        ind('a12', 'Population with access to electricity', '%', 'Ministry of Energy', 37, 2020, 60, 100, 2030, 46),
      ],
    },
    {
      key: 'a2-integrated',
      name: 'An Integrated Continent',
      icon: 'hub',
      kind: 'area',
      group: 'Agenda 2063',
      summary: 'Aspiration 2 — a politically united continent.',
      indicators: [
        ind('a21', 'Intra-African share of exports', '%', 'Ministry of Industry and Trade', 20, 2020, 30, 50, 2030, 24),
        ind('a22', 'AfCFTA tariff schedule implemented', '%', 'Ministry of Industry and Trade', 0, 2021, 60, 100, 2030, 45),
      ],
    },
    {
      key: 'a3-governance',
      name: 'Good Governance & Rule of Law',
      icon: 'gavel',
      kind: 'area',
      group: 'Agenda 2063',
      summary: 'Aspiration 3 — democracy, human rights and justice.',
      indicators: [
        ind('a31', 'Citizens who trust public institutions', '%', 'PO-PSMGG', 60, 2020, 70, 80, 2030, 64),
        ind('a32', "Court cases cleared within the standard time", '%', 'Judiciary', 70, 2020, 85, 95, 2030, 82),
      ],
    },
    {
      key: 'a4-peace',
      name: 'Peace & Security',
      icon: 'shield',
      kind: 'area',
      group: 'Agenda 2063',
      summary: 'Aspiration 4 — a peaceful and secure Africa.',
      indicators: [
        ind('a41', 'Small arms marked and registered', '%', 'Ministry of Home Affairs', 40, 2020, 70, 100, 2030, 58),
      ],
    },
    {
      key: 'a5-culture',
      name: 'Cultural Identity',
      icon: 'theater_comedy',
      kind: 'area',
      group: 'Agenda 2063',
      summary: 'Aspiration 5 — common heritage, values and ethics.',
      indicators: [
        ind('a51', 'Heritage sites with conservation plans', '%', 'Ministry of Natural Resources and Tourism', 50, 2020, 75, 100, 2030, 63),
        ind('a52', 'Countries with Kiswahili teaching programmes', 'count', 'BAKITA', 5, 2020, 15, 30, 2030, 11),
      ],
    },
    {
      key: 'a6-people',
      name: 'People-Driven Development',
      icon: 'groups',
      kind: 'area',
      group: 'Agenda 2063',
      summary: 'Aspiration 6 — unleashing the potential of women and youth.',
      indicators: [
        ind('a61', 'Seats held by women in parliament', '%', 'National Assembly', 37, 2020, 45, 50, 2030, 38),
        ind('a62', 'Youth unemployment rate', '%', "Prime Minister's Office (Labour)", 12, 2020, 10, 7, 2030, 11.4),
      ],
    },
    {
      key: 'a7-global',
      name: 'Strong Global Partner',
      icon: 'language',
      kind: 'area',
      group: 'Agenda 2063',
      summary: 'Aspiration 7 — a self-reliant, influential global player.',
      indicators: [
        ind('a71', 'Development budget funded from domestic sources', '%', 'Ministry of Finance', 65, 2020, 75, 90, 2030, 71),
        ind('a72', 'Tax revenue to GDP', '%', 'Tanzania Revenue Authority', 11.5, 2020, 13, 15, 2030, 12.2),
      ],
    },
  ],
};

// ── Targets and implementation reports, generated from each indicator ───────────

export const REPORT_TYPES: { type: ReportType; label: string; icon: string }[] = [
  { type: 'INPUT', label: 'Input', icon: 'input' },
  { type: 'OUTPUT', label: 'Output', icon: 'output' },
  { type: 'PROCESS', label: 'Process', icon: 'sync' },
  { type: 'RESULT', label: 'Results', icon: 'flag' },
];

const REPORT_TITLES: Record<ReportType, string> = {
  INPUT: 'Funds released and spent',
  PROCESS: 'Quarterly implementation review',
  OUTPUT: 'Outputs delivered and verified',
  RESULT: 'Annual results assessment',
};

/** "69%", "USD 2,000", "300", "1.5 million" — a value in its unit. */
export function formatValue(value: number | null, unit: string): string {
  if (value === null) return '—';
  const n = value.toLocaleString();
  if (unit === '%') return `${n}%`;
  if (unit === 'count') return n;
  if (unit === 'USD') return `USD ${n}`;
  return `${n} ${unit}`;
}

/** Progress from baseline to target value, 0–100. */
export function progressBetween(baseline: number, goal: number, value: number): number {
  if (goal === baseline) return 100;
  return Math.max(0, Math.min(100, Math.round(((value - baseline) / (goal - baseline)) * 100)));
}

function roundLike(value: number, reference: number): number {
  return Math.abs(reference) >= 100 ? Math.round(value) : Math.round(value * 10) / 10;
}

/** Straight-line yearly plan from baseline to target. */
export function linearPlan(
  baseline: number,
  fromYear: number,
  goal: number,
  toYear: number,
): PlanPoint[] {
  const points: PlanPoint[] = [];
  for (let year = fromYear + 1; year <= toYear; year++) {
    const share = (year - fromYear) / (toYear - fromYear);
    points.push({ year, value: roundLike(baseline + (goal - baseline) * share, goal) });
  }
  return points;
}

const SEED_YEAR = 2026;
const SEED_TYPES: ReportType[] = ['INPUT', 'PROCESS', 'OUTPUT', 'RESULT'];

function seedReports(indicator: Indicator, targetId: string, goal: number, dueYear: number) {
  if (indicator.actual === null) return [];
  const last = Math.min(dueYear, SEED_YEAR);
  const years: number[] = [];
  for (let y = Math.max(indicator.baselineYear + 1, last - 3); y <= last; y++) years.push(y);

  return years.map((year, i): ImplementationReport => {
    const share = (i + 1) / years.length;
    const value = roundLike(
      indicator.baseline + ((indicator.actual ?? indicator.baseline) - indicator.baseline) * share,
      goal,
    );
    const type = SEED_TYPES[i % SEED_TYPES.length];
    const quarter = year === SEED_YEAR ? 3 : 4;
    return {
      id: `${targetId}-r${i + 1}`,
      title: REPORT_TITLES[type],
      type,
      year,
      quarter,
      submittedOn: `${year}-${quarter === 3 ? '09' : '12'}-15`,
      reportedBy: indicator.lead,
      summary: `${indicator.name} stood at ${formatValue(value, indicator.unit)} at the end of the period.`,
      value,
      progress: progressBetween(indicator.baseline, goal, value),
      archived: years.length > 3 && i === 0,
    };
  });
}

/** A mid-term and an end target for each indicator; the end target has no baseline or plan yet. */
function seedTargets(indicator: Indicator): Target[] {
  const span = indicator.targetYear - indicator.baselineYear;
  const midYear = indicator.baselineYear + Math.max(1, Math.round(span / 2));
  const current = indicator.actual ?? indicator.baseline;

  return [
    {
      id: `${indicator.id}-t1`,
      name: `${indicator.name}: ${formatValue(indicator.midTarget, indicator.unit)} by ${midYear}`,
      unit: indicator.unit,
      startDate: `${indicator.baselineYear}-07-01`,
      dueDate: `${midYear}-06-30`,
      baseline: indicator.baseline,
      baselineYear: indicator.baselineYear,
      targetValue: indicator.midTarget,
      plan: linearPlan(indicator.baseline, indicator.baselineYear, indicator.midTarget, midYear),
      progress: progressBetween(indicator.baseline, indicator.midTarget, current),
      reports: seedReports(indicator, `${indicator.id}-t1`, indicator.midTarget, midYear),
    },
    {
      id: `${indicator.id}-t2`,
      name: `${indicator.name}: ${formatValue(indicator.target, indicator.unit)} by ${indicator.targetYear}`,
      unit: indicator.unit,
      startDate: `${midYear}-07-01`,
      dueDate: `${indicator.targetYear}-06-30`,
      baseline: null,
      baselineYear: null,
      targetValue: indicator.target,
      plan: [],
      progress: progressBetween(indicator.baseline, indicator.target, current),
      reports: [],
    },
  ];
}

export function interventionTotal(i: Intervention): number {
  return i.government + i.dps + i.privateSector;
}

/** The last four quarters to the seed date. */
const SEED_PERIODS: { year: number; quarter: 1 | 2 | 3 | 4 }[] = [
  { year: 2025, quarter: 4 },
  { year: 2026, quarter: 1 },
  { year: 2026, quarter: 2 },
  { year: 2026, quarter: 3 },
];
const SPEND_WEIGHTS = [0.15, 0.25, 0.3, 0.3];

export function quarterEnd(year: number, quarter: 1 | 2 | 3 | 4): string {
  return `${year}-${['03-31', '06-30', '09-30', '12-31'][quarter - 1]}`;
}

/** Financial reports that add up to the intervention's spend, and progress reports climbing to today. */
function seedInterventionReports(i: Intervention): InterventionReport[] {
  const total = interventionTotal(i);
  const spentToDate = (total * i.financial) / 100;
  let cumulativeWeight = 0;
  return SEED_PERIODS.flatMap(({ year, quarter }, k): InterventionReport[] => {
    const spent = Math.round(spentToDate * SPEND_WEIGHTS[k] * 10) / 10;
    cumulativeWeight += SPEND_WEIGHTS[k];
    const submittedOn = quarterEnd(year, quarter);
    return [
      {
        id: `${i.id}-f${k + 1}`,
        kind: 'FINANCIAL',
        year,
        quarter,
        submittedOn,
        reportedBy: i.implementer,
        released: Math.round(spent * 1.06 * 10) / 10,
        spent,
        financial: null,
        physical: null,
        summary: `Funds released and spent on ${i.project} in the quarter.`,
      },
      {
        id: `${i.id}-p${k + 1}`,
        kind: 'PROGRESS',
        year,
        quarter,
        submittedOn,
        reportedBy: i.implementer,
        released: null,
        spent: null,
        financial: Math.round(i.financial * cumulativeWeight),
        physical: Math.round(i.physical * cumulativeWeight),
        summary: `Works on ${i.project} progressing as reported by ${i.implementer}.`,
      },
    ];
  });
}

function withTargets(framework: FrameworkDefinition): FrameworkDefinition {
  return {
    ...framework,
    processes: framework.processes.map((p) => ({
      ...p,
      indicators: p.indicators?.map((i) => ({ ...i, targets: seedTargets(i) })),
      interventions: p.interventions?.map((i) => ({ ...i, reports: seedInterventionReports(i) })),
    })),
  };
}

export const NATIONAL_FRAMEWORKS: FrameworkDefinition[] = [
  VISION,
  FYDP,
  MANIFESTO,
  STRATEGIC_PLANS,
  FLAGSHIP,
  INTERNATIONAL,
].map(withTargets);

/** Due date passed and not yet reached. */
export function isTargetOverdue(target: Target): boolean {
  return target.progress < 100 && new Date(target.dueDate).getTime() < Date.now();
}

export type TargetStatus = 'COMPLETED' | 'OVERDUE' | 'IN_PROGRESS' | 'NOT_STARTED';

export function targetStatus(target: Target): TargetStatus {
  if (target.progress >= 100) return 'COMPLETED';
  if (isTargetOverdue(target)) return 'OVERDUE';
  if (new Date(target.startDate).getTime() > Date.now()) return 'NOT_STARTED';
  return 'IN_PROGRESS';
}

export function findFramework(key: string | null | undefined): FrameworkDefinition | undefined {
  return NATIONAL_FRAMEWORKS.find((f) => f.key === key);
}

/** Frameworks that implement the given one — its children in the hierarchy. */
export function implementedThrough(key: FrameworkKey): FrameworkDefinition[] {
  return NATIONAL_FRAMEWORKS.filter((f) => f.implements.includes(key));
}
